import { ITag } from '@sdk/contracts';
import { Select } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CounterWrapper, LabelWrapper, Wrapper } from './TagSelector.styles';

import { Icon } from '@/components/Icon';
import { useMyTagsList, useProfile } from '@/store';
import { handleInputValue } from '@/utils';

type Props = {
  value?: string[] | string;
  filters?: string[];
  allowClear?: boolean;
  onChange?: (value: string[] | string) => void;
  mode?: 'multiple' | 'tags';
  maxTagCount?: number | 'responsive';
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  suffixIcon?: React.ReactNode;
};

const DROPDOWN_WIDTH = 300;
const INPUT_WIDTH = 160;

export const TagSelector: React.FC<Props> = ({
  value,
  onChange,
  mode,
  filters,
  allowClear,
  maxTagCount,
  placeholder,
  disabled = false,
  style,
  suffixIcon
}) => {
  const { t } = useTranslation('CourseTranslations');
  const { tags = [], isLoading } = useMyTagsList();
  const {
    profile,
    roleCheck: { isStudent }
  } = useProfile();

  if (!profile) return null;

  const filterTags = (tagObjects: ITag[]) => {
    if (!filters) return tagObjects.map(tag => ({ label: tag.name, value: tag._id }));
    return tagObjects
      .filter(tag => filters.includes(tag._id))
      .map(tag => ({
        label: tag.name,
        value: tag._id
      }));
  };

  const options = isStudent
    ? profile.tags.map(tag => ({ label: tag.name, value: tag._id }))
    : filterTags(tags);

  const hasValue = !!value && (Array.isArray(value) ? value.length > 0 : true);

  return (
    <Wrapper $hasValue={hasValue}>
      <Select
        showSearch
        variant="borderless"
        allowClear={allowClear}
        mode={mode}
        maxTagCount={maxTagCount ?? 0}
        maxTagPlaceholder={() => (
          <LabelWrapper>
            <div>{placeholder ?? t('TAG')}</div>
            {hasValue && (
              <CounterWrapper $isDisabled={disabled}>
                {Array.isArray(value) ? value.length : 1}
              </CounterWrapper>
            )}
          </LabelWrapper>
        )}
        options={options}
        onChange={onChange}
        loading={isLoading}
        placeholder={placeholder ?? t('TAG')}
        value={handleInputValue(value, mode === 'multiple')}
        filterOption={(input, option) =>
          (option?.label.toLocaleLowerCase() ?? '').includes(input.toLocaleLowerCase())
        }
        suffixIcon={suffixIcon ?? <Icon i="CaretDown" />}
        style={{ minWidth: INPUT_WIDTH, ...style }}
        styles={{ popup: { root: { minWidth: DROPDOWN_WIDTH } } }}
        disabled={disabled}
      />
    </Wrapper>
  );
};
